/**
 * Example Tab Module
 * SPでカテゴリタブをクリックすると該当カテゴリのみ表示
 */
export class ExampleTabs {
  constructor() {
    this.root = document.querySelector('.l-example');
    if (!this.root) {
      return;
    }

    this.tabs = Array.from(this.root.querySelectorAll('.l-example__tab-button'));
    this.items = Array.from(this.root.querySelectorAll('.l-example__item'));
    this.list = this.root.querySelector('.l-example__list');
    this.indicator = this.root.querySelector('.l-example__indicator');
    this.indicatorThumb = this.root.querySelector('.l-example__indicator-thumb');
    this.more = this.root.querySelector('.l-example__more');
    this.moreButton = this.root.querySelector('.l-example__more-btn');
    this.visibleCount = 2;
    this.pcMedia = window.matchMedia('(min-width: 768px)');
    this.resizeRaf = null;
    this.activeArea = null;
    this.isExpanded = false;
    this.prevVisibleCount = this.visibleCount;

    this.onResize = this.handleResize.bind(this);
    this.onListScroll = this.updateIndicator.bind(this);

    if (this.tabs.length === 0) {
      return;
    }

    this.setupTabs();
    this.bindEvents();
    this.activate(this.tabs.find((tab) => tab.classList.contains('is-active')) || this.tabs[0], false);
  }

  bindEvents() {
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        this.activate(tab, true);
      });
    });

    this.moreButton?.addEventListener('click', () => {
      this.expand();
    });

    if (this.list) {
      this.list.addEventListener('scroll', this.onListScroll, { passive: true });
    }

    window.addEventListener('resize', this.onResize);
  }

  setupTabs() {
    this.root.querySelector('.l-example__tab-list')?.setAttribute('role', 'tablist');

    this.tabs.forEach((tab) => {
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-selected', tab.classList.contains('is-active') ? 'true' : 'false');
    });
  }

  destroy() {
    window.removeEventListener('resize', this.onResize);
    if (this.list) {
      this.list.removeEventListener('scroll', this.onListScroll);
    }
    if (this.resizeRaf) {
      cancelAnimationFrame(this.resizeRaf);
      this.resizeRaf = null;
    }
  }

  handleResize() {
    if (this.resizeRaf) {
      cancelAnimationFrame(this.resizeRaf);
    }

    this.resizeRaf = requestAnimationFrame(() => {
      this.resizeRaf = null;
      this.updateScrollState();
    });
  }

  activate(activeTab, animateList = false) {
    this.activeArea = activeTab.dataset.tab;
    this.isExpanded = false;
    this.prevVisibleCount = this.visibleCount;

    this.tabs.forEach((tab) => {
      tab.classList.toggle('is-active', tab === activeTab);
      tab.setAttribute('aria-selected', tab === activeTab ? 'true' : 'false');
    });

    this.updateVisibleItems();

    if (animateList && this.list) {
      this.list.classList.remove('is-animating');
      void this.list.offsetWidth;
      this.list.classList.add('is-animating');
    }

    if (this.list) {
      this.list.scrollTo({ left: 0, behavior: 'auto' });
    }

    this.updateScrollState();
  }

  expand() {
    this.prevVisibleCount = this.visibleCount;
    this.isExpanded = true;
    this.updateVisibleItems(true);
    this.updateScrollState();
  }

  updateVisibleItems(animateNew = false) {
    let visibleIndex = 0;
    let areaItemCount = 0;

    this.items.forEach((item) => {
      const wasVisible = !item.hasAttribute('hidden');

      if (item.dataset.area !== this.activeArea) {
        item.setAttribute('hidden', '');
        item.classList.remove('is-enter');
        item.classList.remove('is-enter-active');
        return;
      }

      areaItemCount += 1;

      if (!this.isExpanded && visibleIndex >= this.visibleCount) {
        item.setAttribute('hidden', '');
        item.classList.remove('is-enter');
        item.classList.remove('is-enter-active');
        return;
      }

      item.removeAttribute('hidden');
      const isNewItem = animateNew && !wasVisible && visibleIndex >= this.prevVisibleCount;

      if (isNewItem) {
        item.classList.add('is-enter');
        item.classList.remove('is-enter-active');
        requestAnimationFrame(() => {
          item.classList.add('is-enter-active');
        });
      } else {
        item.classList.remove('is-enter');
        item.classList.remove('is-enter-active');
      }

      visibleIndex += 1;
    });

    if (this.more) {
      this.more.hidden = this.isExpanded || areaItemCount <= this.visibleCount;
    }
  }

  updateScrollState() {
    if (!this.list || !this.indicator || !this.indicatorThumb) {
      return;
    }

    const visibleItems = this.items.filter((item) => !item.hasAttribute('hidden'));
    const shouldScroll = !this.pcMedia.matches && visibleItems.length >= this.visibleCount;

    this.root.classList.toggle('is-scrollable', shouldScroll);

    if (!shouldScroll) {
      this.indicatorThumb.style.width = '100%';
      this.indicatorThumb.style.left = '0';
      return;
    }

    this.updateIndicator();
  }

  updateIndicator() {
    if (!this.list || !this.indicatorThumb) {
      return;
    }

    const maxScroll = this.list.scrollWidth - this.list.clientWidth;
    const progress = maxScroll > 0 ? this.list.scrollLeft / maxScroll : 0;
    const thumbWidth = maxScroll > 0 ? progress * 100 : 100;

    this.indicatorThumb.style.width = `${thumbWidth}%`;
    this.indicatorThumb.style.left = '0';
  }
}
